import {isEqual} from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import AccordionSection from '@contentlab/components/accordion-section';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewTransitionEvent from '@contentlab/hooks/analytics/use-view-transition-event';
import contentLabStore, {
  AvatarSize,
  contentLabActions,
  DEFAULT_AVATAR_SPECS_CONFIG,
} from '@contentlab/stores/contentLabStore';
import {ConfigMode} from '@contentlab/types/aiAvatars';
import ElementPosition from '@contentlab/types/elementPosition';
import ConfigMenu from '@contentlab/views/contentlab/pages/ai-avatar-page/components/config-menu/ConfigMenu';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {RgbaColor} from '@/types/rgbaColor';
import AvatarBackgroundSection from '../sections/AvatarBackgroundSection';
import AvatarPositionSection from '../sections/AvatarPositionSection';
import AvatarSizeSection from '../sections/AvatarSizeSection';

const defaultAvatarSpecConfig = DEFAULT_AVATAR_SPECS_CONFIG();

enum AvatarConfigMenuSection {
  POSITION = 'position',
  BACKGROUND_SETTINGS = 'background_settings',
  CHARACTER_SIZE = 'character_size',
}

interface AvatarConfigMenuProps {
  testID?: string;
  onClose?: () => void;
}

function AvatarConfigMenu({
  testID = 'avatar-config-menu',
  onClose,
}: AvatarConfigMenuProps) {
  const {draftAiAvatarProject, configMode} = contentLabStore((state) => ({
    draftAiAvatarProject: state.draftAiAvatarProject,
    configMode: state.draftAiAvatarProject.configMode,
  }));
  const avatarId = draftAiAvatarProject.avatarSpec.libraryId;

  const {uiInteraction} = useUiInteractionEvent();
  const viewTransition = useViewTransitionEvent();

  useEffect(() => {
    const presentationId = uuid();
    const startTime = Date.now();
    viewTransition.presented({
      presentation_id: presentationId,
      view_name: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
      view_type: 'view',
      view_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
      source: 'clicked Adjust Layout',
    });

    return () => {
      const endTime = Date.now();
      viewTransition.dismissed({
        interaction_id: uuid(),
        presentation_id: presentationId,
        reason: 'closed by user',
        view_details: `Total presented time: ${endTime - startTime} ms`,
      });
    };
  }, []);

  const getCurrentConfig = () => {
    const spec = draftAiAvatarProject.avatarSpec;
    if (configMode === ConfigMode.LIBRARY && spec.configLibrary) {
      return spec.configLibrary;
    }
    if (configMode === ConfigMode.CUSTOM && spec.configCustom) {
      return spec.configCustom;
    }
    return spec.config;
  };

  const currentConfig = getCurrentConfig();

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    [AvatarConfigMenuSection.POSITION]: true,
    [AvatarConfigMenuSection.BACKGROUND_SETTINGS]: false,
    [AvatarConfigMenuSection.CHARACTER_SIZE]: false,
  });

  const handleSectionChange = useCallback(
    // Collapse all sections except the one that is being expanded
    (section: string, expanded: boolean) => {
      setExpandedSections({
        [AvatarConfigMenuSection.POSITION]:
          section === AvatarConfigMenuSection.POSITION && expanded,
        [AvatarConfigMenuSection.BACKGROUND_SETTINGS]:
          section === AvatarConfigMenuSection.BACKGROUND_SETTINGS && expanded,
        [AvatarConfigMenuSection.CHARACTER_SIZE]:
          section === AvatarConfigMenuSection.CHARACTER_SIZE && expanded,
      });

      uiInteraction({
        item_name: 'avatar_layout_menu_section_toggle',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
        interaction_details: JSON.stringify({
          section,
          expanded,
        }),
      });
    },
    []
  );

  const handleRevert = useCallback(() => {
    setExpandedSections({
      [AvatarConfigMenuSection.POSITION]: true,
      [AvatarConfigMenuSection.BACKGROUND_SETTINGS]: false,
      [AvatarConfigMenuSection.CHARACTER_SIZE]: false,
    });

    contentLabActions.updateDraftAiAvatarProjectConfig({
      ...defaultAvatarSpecConfig,
    });

    uiInteraction({
      item_name: 'avatar_layout_menu_revert_button',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
      asset_id: avatarId || '',
    });
  }, [avatarId]);

  const handlePositionChange = useCallback(
    (position: ElementPosition) => {
      contentLabActions.updateDraftAiAvatarProjectConfig({
        avatarPosition: position,
      });

      uiInteraction({
        item_name: 'avatar_position_change',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
        interaction_details: position,
        asset_id: avatarId || '',
      });
    },
    [avatarId]
  );

  const handleBackgroundChange = useCallback(
    (removeBackground: boolean, backgroundColor?: RgbaColor) => {
      contentLabActions.updateDraftAiAvatarProjectConfig({
        removeBackground,
        avatarColor: backgroundColor,
      });

      uiInteraction({
        item_name: 'avatar_background_change',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
        interaction_details: JSON.stringify({
          remove_background: removeBackground,
          background_color: backgroundColor,
        }),
        asset_id: avatarId || '',
      });
    },
    [avatarId]
  );

  const toggleCircleMask = useCallback(
    (circleMask?: boolean) => {
      const newCircleMask =
        circleMask !== undefined ? circleMask : !currentConfig.circleMask;
      contentLabActions.updateDraftAiAvatarProjectConfig({
        circleMask: newCircleMask,
      });
      uiInteraction({
        item_name: 'avatar_circle_mask_change',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
        interaction_details: newCircleMask ? 'on' : 'off',
        asset_id: avatarId || '',
      });
    },
    [currentConfig.circleMask, avatarId]
  );

  const handleAvatarSizeChange = useCallback(
    (size: AvatarSize) => {
      contentLabActions.updateDraftAiAvatarProjectConfig({
        avatarSize: size,
      });

      uiInteraction({
        item_name: 'avatar_size_change',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
        interaction_details: size,
        asset_id: avatarId || '',
      });
    },
    [avatarId]
  );

  const toggleCropAvatar = useCallback(() => {
    const newCropAvatar = !currentConfig.cropAvatar;
    contentLabActions.updateDraftAiAvatarProjectConfig({
      cropAvatar: newCropAvatar,
    });

    uiInteraction({
      item_name: 'avatar_crop_change',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_LAYOUT,
      interaction_details: newCropAvatar ? 'on' : 'off',
      asset_id: avatarId || '',
    });
  }, [currentConfig.cropAvatar, avatarId]);

  return (
    <ConfigMenu
      testID={testID}
      title={translate('Adjust Layout')}
      onClose={onClose}
      topSectionButtons={[
        {
          label: translate('Revert'),
          icon: 'Actions-Reset',
          appearance: 'neutral',
          mode: 'plain',
          onClick: handleRevert,
          isDisabled: isEqual(currentConfig, defaultAvatarSpecConfig),
          tooltipLabel: translate('No changes to revert'),
        },
      ]}
      accordionSections={[
        <AccordionSection
          key={AvatarConfigMenuSection.POSITION}
          testID={AvatarConfigMenuSection.POSITION}
          sectionKey={AvatarConfigMenuSection.POSITION}
          title={translate('Position')}
          iconName="Actions-Move"
          expanded={expandedSections[AvatarConfigMenuSection.POSITION]}
          onToggle={handleSectionChange}
          variant="compact"
        >
          <AvatarPositionSection
            testID="avatar-position-section"
            selectedPosition={currentConfig.avatarPosition}
            onPositionSelect={handlePositionChange}
          />
        </AccordionSection>,
        <AccordionSection
          key={AvatarConfigMenuSection.BACKGROUND_SETTINGS}
          testID={AvatarConfigMenuSection.BACKGROUND_SETTINGS}
          sectionKey={AvatarConfigMenuSection.BACKGROUND_SETTINGS}
          title={translate('Background Settings')}
          iconName="Background-Settings"
          expanded={
            expandedSections[AvatarConfigMenuSection.BACKGROUND_SETTINGS]
          }
          onToggle={handleSectionChange}
          variant="compact"
        >
          <AvatarBackgroundSection
            testID="avatar-background-settings-section"
            removeBackground={currentConfig.removeBackground}
            backgroundColor={currentConfig.avatarColor || undefined}
            handleBackgroundChange={handleBackgroundChange}
            circleMask={currentConfig.circleMask}
            toggleCircleMask={toggleCircleMask}
          />
        </AccordionSection>,
        <AccordionSection
          key={AvatarConfigMenuSection.CHARACTER_SIZE}
          testID={AvatarConfigMenuSection.CHARACTER_SIZE}
          sectionKey={AvatarConfigMenuSection.CHARACTER_SIZE}
          title={translate('Character Size')}
          iconName="Actions-CharacterSize"
          expanded={expandedSections[AvatarConfigMenuSection.CHARACTER_SIZE]}
          onToggle={handleSectionChange}
          variant="compact"
        >
          <AvatarSizeSection
            testID="avatar-size-section"
            avatarSize={currentConfig.avatarSize}
            onSizeChange={handleAvatarSizeChange}
            cropAvatar={currentConfig.cropAvatar}
            toggleCropAvatar={toggleCropAvatar}
          />
        </AccordionSection>,
      ]}
    />
  );
}

export default AvatarConfigMenu;
